import { useEffect } from 'react';
import { Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormDialog } from '../../../shared/components/FormDialog';
import { DatePickerPtBrField } from '../../../shared/components/DatePickerPtBr';
import { ColaboradorSelect } from '../../../shared/components/ColaboradorSelect';
import { describeApiError } from '../../../shared/api/errors';
import { createEmployeeFeriasApi } from '../ferias/api';
import type { FeriaGlobalListItem } from './types';

const dateRe = /^\d{4}-\d{2}-\d{2}$/;

const schema = z
  .object({
    colaboradorCodigo: z
      .number({ invalid_type_error: 'Selecione um colaborador' })
      .int()
      .positive('Selecione um colaborador'),
    dataInicio: z.string().regex(dateRe, 'Data inválida (use AAAA-MM-DD)'),
    dataFim: z.string().regex(dateRe, 'Data inválida (use AAAA-MM-DD)'),
    observacao: z.string().max(500, 'Máximo 500 caracteres'),
  })
  .superRefine((v, ctx) => {
    if (v.dataInicio && v.dataFim && v.dataFim < v.dataInicio) {
      ctx.addIssue({
        path: ['dataFim'],
        code: 'custom',
        message: 'Data fim deve ser maior ou igual à data de início.',
      });
    }
  });

type FormValues = z.infer<typeof schema>;

export interface FeriaGlobalFormDialogProps {
  open: boolean;
  /** Linha em edição, ou `null` para novo período. */
  feria: FeriaGlobalListItem | null;
  onClose: () => void;
}

/**
 * Cria/edita um período de Férias a partir da visão global. Em criação o
 * colaborador é escolhido no dialog → o submit chama o sub-recurso
 * `/employees/{colaboradorCodigo}/ferias`. Em edição o colaborador e a data de
 * início ficam travados (PK = dataInicio). A regra de anti-sobreposição é do
 * server — o 409 aparece via `serverError`.
 */
export function FeriaGlobalFormDialog({
  open,
  feria,
  onClose,
}: FeriaGlobalFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isEdit = feria !== null;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      colaboradorCodigo: 0,
      dataInicio: new Date().toISOString().slice(0, 10),
      dataFim: new Date().toISOString().slice(0, 10),
      observacao: '',
    },
  });

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const nested = createEmployeeFeriasApi(values.colaboradorCodigo);
      const input = {
        dataInicio: values.dataInicio,
        dataFim: values.dataFim,
        observacao: values.observacao,
      };
      if (isEdit) return nested.update(values.dataInicio, input);
      return nested.create(input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ferias', 'global'] });
      onClose();
    },
  });

  useEffect(() => {
    if (!open) return;
    mutation.reset();
    if (feria) {
      form.reset({
        colaboradorCodigo: feria.employeeId,
        dataInicio: feria.dataInicio,
        dataFim: feria.dataFim,
        observacao: feria.observacao ?? '',
      });
    } else {
      const hoje = new Date().toISOString().slice(0, 10);
      form.reset({ colaboradorCodigo: 0, dataInicio: hoje, dataFim: hoje, observacao: '' });
    }
    // mutation é estável (react-query); reset limpa erro anterior ao reabrir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, feria, form]);

  const serverError = mutation.isError ? describeApiError(mutation.error) : null;

  const onSubmit = async (values: FormValues): Promise<void> => {
    try {
      await mutation.mutateAsync(values);
    } catch {
      /* erro exibido via serverError (mutation.error) */
    }
  };

  return (
    <FormDialog
      open={open}
      title={
        isEdit
          ? t('features.colaboradores.feriasGlobal.dialog.editTitle')
          : t('features.colaboradores.feriasGlobal.dialog.newTitle')
      }
      form={form}
      onSubmit={onSubmit}
      onCancel={onClose}
      serverError={serverError}
      maxWidth="sm"
    >
      <Stack spacing={2}>
        {isEdit ? (
          <Typography variant="subtitle2">{feria?.colaboradorNome}</Typography>
        ) : (
          <Controller
            control={form.control}
            name="colaboradorCodigo"
            render={({ field, fieldState }) => (
              <ColaboradorSelect
                value={field.value > 0 ? field.value : null}
                onChange={(id) => field.onChange(id ?? 0)}
                label={t('features.colaboradores.feriasGlobal.fields.colaborador')}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
        )}

        <DatePickerPtBrField
          control={form.control}
          name="dataInicio"
          label={t('features.colaboradores.ferias.fields.dataInicio')}
          disabled={isEdit}
          slotProps={{
            textField: {
              fullWidth: true,
              helperText: isEdit
                ? t('features.colaboradores.ferias.fields.dataInicioLockedHelper')
                : t('features.colaboradores.ferias.fields.dataInicioHelper'),
            },
          }}
        />

        <DatePickerPtBrField
          control={form.control}
          name="dataFim"
          label={t('features.colaboradores.ferias.fields.dataFim')}
          slotProps={{
            textField: {
              fullWidth: true,
              helperText:
                form.formState.errors.dataFim?.message ??
                t('features.colaboradores.ferias.fields.dataFimHelper'),
              error: Boolean(form.formState.errors.dataFim),
            },
          }}
        />

        <TextField
          label={t('features.colaboradores.ferias.fields.observacao')}
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          inputProps={{ maxLength: 500 }}
          error={Boolean(form.formState.errors.observacao)}
          helperText={
            form.formState.errors.observacao?.message ??
            t('features.colaboradores.ferias.fields.observacaoHelper')
          }
          {...form.register('observacao')}
        />
      </Stack>
    </FormDialog>
  );
}
